import { asCents, asMajorUnitAmount } from '@/core/types/money';
import type { Bp, Cents, MajorUnitAmount } from '@/core/types/money';

// ── Date/time display (constitution 5c) ──────────────────────────────────
// Store-scoped data MUST be displayed in the store's timezone — the viewer
// (a remote owner) may be in another timezone while the backend buckets
// business days via BusinessClock. `timezone` is therefore mandatory; there
// is no silent browser-timezone fallback. An invalid IANA string throws —
// surfacing the bug beats silently showing a wrong calendar day.
//
// Tenant-scoped data (audit trail, billing periods, cross-store dashboards)
// has no single store timezone; use the explicit `*ViewerLocal` variants.

export function formatDate(ms: number, timezone: string): string {
  return new Date(ms).toLocaleDateString(undefined, { timeZone: timezone });
}

export function formatDateTime(ms: number, timezone: string): string {
  return new Date(ms).toLocaleString(undefined, { timeZone: timezone, hour12: false });
}

/** Normalize optional/sentinel epoch millis before any display formatter sees it. */
export function optionalEpochMillis(ms: number | null | undefined): number | null {
  if (ms == null || ms <= 0 || !Number.isFinite(ms)) return null;
  return Number.isNaN(new Date(ms).getTime()) ? null : ms;
}

/** Store-scoped optional epoch display. Sentinel/non-date values render as absent. */
export function formatOptionalEpochMillis(
  ms: number | null | undefined,
  timezone: string,
): string | null {
  const value = optionalEpochMillis(ms);
  return value == null ? null : formatDateTime(value, timezone);
}

export function formatTime(ms: number, timezone: string): string {
  return new Date(ms).toLocaleTimeString(undefined, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

export function formatTimeSec(ms: number, timezone: string): string {
  return new Date(ms).toLocaleTimeString(undefined, {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

/** Tenant-scope ONLY (audit/billing/multi-store dashboard): viewer's local timezone, explicitly chosen. Never use for store-scoped data. */
export function formatDateViewerLocal(ms: number): string {
  return new Date(ms).toLocaleDateString();
}

/** Tenant-scoped optional epoch display in the explicitly selected viewer timezone. */
export function formatOptionalEpochViewerLocal(
  ms: number | null | undefined,
): string | null {
  const value = optionalEpochMillis(ms);
  return value == null ? null : formatDateViewerLocal(value);
}

/** Tenant-scope ONLY: viewer's local calendar date (YYYY-MM-DD), offset by N days. */
export function viewerLocalYmd(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** Tenant-scope ONLY — see formatDateViewerLocal. */
export function formatDateTimeViewerLocal(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { hour12: false });
}

/** Tenant-scope ONLY — see formatDateViewerLocal. */
export function formatTimeViewerLocal(ms: number): string {
  return new Date(ms).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false });
}

// ── Currency display ─────────────────────────────────────────────────────
// 与上面的时区规则同构:币种是**门店税务辖区的事实**(`Jurisdiction → Currency`,
// 镜像 Rust `StoreInfo::currency_symbol`),不是默认值。`currency` 因此是必传参数,
// 没有静默的 `?? 'EUR'` 兜底 —— 那会让任何非欧元门店的每一个金额都标错币种,而且
// 因为今天所有门店都在西班牙,错得完全看不出来。
//
// 租户作用域的金额没有单一门店币种,用下面两个显式变体,别绕过必传参数。

/** 平台计费币种。Stripe 订阅/套餐以此计价,与门店在哪个辖区**无关**。 */
const PLATFORM_CURRENCY = 'EUR';

// Formatter cache keyed by locale:currency
const currencyFormatters = new Map<string, Intl.NumberFormat>();

export function formatCurrency(
  amount: MajorUnitAmount,
  currency: string,
  locale?: string,
): string {
  // locale 省略时退回观看者浏览器 —— 这只对**租户作用域**成立(跨店聚合、平台账单
  // 没有单一门店 locale)。门店作用域一律走 `useStoreMoney()`,它显式传门店的
  // `locale_bcp47`:金额写法要和顾客手里的小票一致,而小票由 edge-server 用门店 locale 打印。
  const resolvedLocale = locale ?? navigator.language ?? 'es-ES';
  const key = `${resolvedLocale}:${currency}`;
  let fmt = currencyFormatters.get(key);
  if (!fmt) {
    fmt = new Intl.NumberFormat(resolvedLocale, { style: 'currency', currency });
    currencyFormatters.set(key, fmt);
  }
  return fmt.format(amount);
}

export function formatMoney(cents: Cents, currency: string, locale?: string): string {
  return formatCurrency(centsToMajorUnitAmount(cents), currency, locale);
}

/**
 * 平台计费金额 —— 订阅、套餐价、Stripe 发票。
 *
 * 刻意**不**跟门店辖区走:一家日元门店的订阅仍然是 €100/月,按门店币种渲染会把它
 * 显示成 ¥100。这是本文件里唯一一个正当的固定币种。
 */
export function formatPlatformMoney(cents: Cents): string {
  return formatMoney(cents, PLATFORM_CURRENCY);
}

/**
 * 租户级跨店聚合金额(总营收、门店趋势对比)。
 *
 * **前提**:该租户名下所有门店同币种。这在今天成立(Jurisdiction 只有
 * `SpainPeninsula` / `SpainCanary`,都是 EUR),但一旦出现跨币种租户,**求和本身**
 * 就失去意义 —— 那时要改的是聚合口径(按币种分组或换算),不只是这里的符号。
 * 单独命名而不是复用 `formatPlatformMoney`,是为了让那天能一眼找到所有受影响的点。
 */
export function formatTenantAggregateMoney(cents: Cents): string {
  return formatMoney(cents, PLATFORM_CURRENCY);
}

export function centsToMajorUnitAmount(cents: Cents): MajorUnitAmount {
  return asMajorUnitAmount(cents / 100);
}

export function majorUnitAmountToCents(amount: MajorUnitAmount): Cents {
  return asCents(Math.round(amount * 100));
}

export function formatPct(bp: Bp): string {
  return `${((bp as number) / 100).toFixed(2)}%`;
}

export function timeAgo(ms: number): string {
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}
